import { useMemo, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading } from "@/components/pawkt/Empty";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { SlidersHorizontal, ArrowUp, ArrowDown, Info, RotateCcw, Save, Gift } from "lucide-react";

const CALC = ["package", "membership", "coupon", "manual"]; // loyalty is policy-gate only

export default function CommercialRules() {
  const qc = useQueryClient();
  const [branchId, setBranchId] = useState(""); // "" = tenant default

  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: async () => (await api.get("/branches")).data });
  const { data, isLoading } = useQuery({
    queryKey: ["commercial-rules", branchId],
    queryFn: async () => (await api.get(`/commercial-rules?branch_id=${branchId}`)).data,
  });

  const [draft, setDraft] = useState(null);
  // (re)seed the editor whenever the resolved policy for the selection changes
  const seedKey = `${branchId}:${data?.resolved?.version ?? ""}:${data?.resolved?.scope ?? ""}`;
  useEffect(() => {
    if (!data) return;
    const r = data.resolved;
    setDraft({ enabled: { ...r.enabled }, order: [...r.order], exclusions: r.exclusions.map((p) => [...p]) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey]);

  const sources = data?.sources || ["package", "membership", "coupon", "loyalty", "manual"];
  const labels = data?.source_labels || {};
  const isBranch = !!branchId;
  const hasOverride = isBranch && (data?.branch_overrides || []).some((o) => o.branch_id === branchId);
  const inherited = isBranch && !hasOverride;

  const save = useMutation({
    mutationFn: (b) => api.put("/commercial-rules", b),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["commercial-rules"] }); toast.success("Commercial rules saved"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const removeOverride = useMutation({
    mutationFn: () => api.delete(`/commercial-rules/${branchId}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["commercial-rules"] }); toast.success("Branch override removed — using tenant default"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const pairs = useMemo(() => {
    const out = [];
    for (let i = 0; i < sources.length; i++)
      for (let j = i + 1; j < sources.length; j++) out.push([sources[i], sources[j]]);
    return out;
  }, [sources]);

  if (isLoading || !draft) return <Loading />;

  const toggle = (s, v) => setDraft((d) => ({ ...d, enabled: { ...d.enabled, [s]: v } }));
  const move = (idx, dir) => setDraft((d) => {
    const order = [...d.order];
    const j = idx + dir;
    if (j < 0 || j >= order.length) return d;
    [order[idx], order[j]] = [order[j], order[idx]];
    return { ...d, order };
  });
  const isExcluded = (a, b) => draft.exclusions.some((p) => (p[0] === a && p[1] === b) || (p[0] === b && p[1] === a));
  const setExcluded = (a, b, excluded) => setDraft((d) => {
    const rest = d.exclusions.filter((p) => !((p[0] === a && p[1] === b) || (p[0] === b && p[1] === a)));
    return { ...d, exclusions: excluded ? [...rest, [a, b]] : rest };
  });

  const onSave = () => save.mutate({ branch_id: branchId, enabled: draft.enabled, order: draft.order, exclusions: draft.exclusions });

  return (
    <div data-testid="commercial-rules-page">
      <PageHeader title="Commercial Rules" subtitle="Configure how package, membership, coupon, loyalty & manual discounts work together" />

      {/* scope selector */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 mb-5">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-slate-600 flex items-center gap-2"><SlidersHorizontal className="h-4 w-4 text-slate-400" /> Applies to</span>
          <select data-testid="commercial-scope-select" value={branchId}
            onChange={(e) => { setBranchId(e.target.value); }}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
            <option value="">All branches (tenant default)</option>
            {(branches || []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          {!isBranch && <span className="text-xs text-slate-400">Default policy for all branches unless a branch has its own override.</span>}
          {inherited && <span data-testid="inherited-badge" className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">Inherited from tenant</span>}
          {hasOverride && <span data-testid="override-badge" className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">Branch override</span>}
        </div>
        {inherited && <p className="mt-2 text-xs text-slate-500">This branch currently follows the tenant default. Editing & saving here creates a branch-specific override.</p>}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* enable / disable */}
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="enabled-card">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-1">Benefit types</h3>
          <p className="text-xs text-slate-400 mb-4">Turn each commercial benefit on or off for this policy.</p>
          <div className="space-y-2.5">
            {sources.map((s) => (
              <div key={s} className="flex items-center justify-between rounded-lg border border-slate-200 px-3.5 py-3">
                <div>
                  <span className="text-sm font-medium text-slate-700">{labels[s] || s}</span>
                  {s === "loyalty" && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-600"><Gift className="h-3 w-3" /> policy only</span>}
                </div>
                <Switch data-testid={`enable-${s}`} checked={!!draft.enabled[s]} onCheckedChange={(v) => toggle(s, v)} />
              </div>
            ))}
          </div>
          <p className="mt-3 flex items-start gap-1.5 text-xs text-slate-400"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Loyalty is a policy control: it decides whether loyalty redemption is permitted — it does not automatically apply loyalty to a checkout.</p>
        </div>

        {/* order */}
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="order-card">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-1">Benefit order</h3>
          <p className="text-xs text-slate-400 mb-4">Benefits are applied top-to-bottom. Tax is always applied last.</p>
          <div className="space-y-2">
            {draft.order.map((s, idx) => (
              <div key={s} data-testid={`order-row-${s}`} className="flex items-center justify-between rounded-lg border border-slate-200 px-3.5 py-2.5">
                <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-xs text-slate-500">{idx + 1}</span>
                  {labels[s] || s}
                  {!CALC.includes(s) && <span className="text-[10px] text-slate-400">(policy)</span>}
                </span>
                <span className="flex gap-1">
                  <button data-testid={`order-up-${s}`} disabled={idx === 0} onClick={() => move(idx, -1)} className="rounded-md p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                  <button data-testid={`order-down-${s}`} disabled={idx === draft.order.length - 1} onClick={() => move(idx, 1)} className="rounded-md p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* combination rules */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 lg:col-span-2" data-testid="combination-card">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-1">Combination rules</h3>
          <p className="text-xs text-slate-400 mb-4">Choose which benefits may be combined. Mutually-exclusive benefits never stack — the higher-priority one wins.</p>
          <div className="grid gap-2.5 sm:grid-cols-2">
            {pairs.map(([a, b]) => {
              const bothOn = draft.enabled[a] && draft.enabled[b];
              const excluded = isExcluded(a, b);
              return (
                <div key={`${a}-${b}`} className={`flex items-center justify-between rounded-lg border px-3.5 py-3 ${bothOn ? "border-slate-200" : "border-slate-100 opacity-50"}`}>
                  <span className="text-sm font-medium text-slate-700">{labels[a] || a} + {labels[b] || b}</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold ${excluded ? "text-rose-600" : "text-emerald-600"}`}>{excluded ? "Not allowed" : "Allowed"}</span>
                    <Switch data-testid={`allow-${a}-${b}`} disabled={!bothOn} checked={!excluded} onCheckedChange={(v) => setExcluded(a, b, !v)} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* actions */}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button data-testid="save-commercial-rules" onClick={onSave} disabled={save.isPending} className="gap-2 bg-slate-900 text-white">
          <Save className="h-4 w-4" /> {isBranch ? (hasOverride ? "Save branch override" : "Create branch override") : "Save tenant default"}
        </Button>
        {hasOverride && (
          <Button data-testid="remove-override-btn" variant="outline" onClick={() => removeOverride.mutate()} disabled={removeOverride.isPending} className="gap-2 text-rose-600 border-rose-200 hover:bg-rose-50">
            <RotateCcw className="h-4 w-4" /> Remove override (use tenant default)
          </Button>
        )}
        <span className="text-xs text-slate-400">Changes apply to future checkouts only — existing invoices keep the rules that were active when they were billed.</span>
      </div>
    </div>
  );
}
